interface OperationQueueState {
  activeCount: number;
  maxInFlight: number;
  waitQueue: Array<() => void>;
}

const operationQueues = new Map<string, OperationQueueState>();

function getQueueState(queueKey: string, maxInFlight: number): OperationQueueState {
  const existing = operationQueues.get(queueKey);
  if (existing) return existing;
  const state: OperationQueueState = { activeCount: 0, maxInFlight, waitQueue: [] };
  operationQueues.set(queueKey, state);
  return state;
}

async function acquireSlot(state: OperationQueueState): Promise<void> {
  if (state.activeCount < state.maxInFlight) {
    state.activeCount++;
    return;
  }
  return new Promise<void>((resolve) => {
    state.waitQueue.push(() => {
      state.activeCount++;
      resolve();
    });
  });
}

function releaseSlot(state: OperationQueueState): void {
  state.activeCount--;
  if (state.waitQueue.length > 0) {
    const next = state.waitQueue.shift()!;
    next();
  }
}

/**
 * Queues async operations by key with a configurable concurrency limit.
 * - maxInFlight = 1 (default): operations execute serially in FIFO order
 * - maxInFlight > 1: up to N operations run concurrently, the rest wait in FIFO order
 */
export async function withOperationQueue<T>(
  queueKey: string,
  fn: () => Promise<T>,
  maxInFlight: number = 1
): Promise<T> {
  const state = getQueueState(queueKey, maxInFlight);
  await acquireSlot(state);
  try {
    return await fn();
  } finally {
    releaseSlot(state);
  }
}
